import { useEffect, useRef } from "react";

/**
 * Re-invokes `fn` every `intervalMs` while the component is mounted and the document is visible.
 * The first call is left to the caller — pair this with whatever initial load you already do.
 *
 * Pausing: pass `enabled={false}` to suspend polling without unmounting the consumer.
 */
export function usePolling(fn: () => unknown | Promise<unknown>, intervalMs: number, enabled = true): void {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!enabled || intervalMs <= 0) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      // Skip tick while the tab is hidden — saves bandwidth and resumes cleanly when visible.
      if (typeof document === "undefined" || document.visibilityState === "visible") {
        try { await fnRef.current(); } catch { /* fn is responsible for surfacing its own errors */ }
      }
      if (cancelled) return;
      timer = setTimeout(tick, intervalMs);
    };

    timer = setTimeout(tick, intervalMs);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [intervalMs, enabled]);
}
